<template>
  <div class="rentme-setup-container">
    <div class="title-bar">
      <span>Rent Me</span>
      <div class="title-buttons">
        <button class="title-button" @click="$emit('close')" title="Close">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <div class="rentme-toolbar">
      <input
        v-model="searchQuery"
        type="text"
        class="search-input"
        placeholder="Search address, serial, model, type..."
        @input="debouncedSearch"
      />
      <div class="action-buttons">
        <button
          :disabled="!selectedItem || showForm"
          @click="startEdit"
          class="btn-action"
          title="Edit"
        >
          <i class="fas fa-edit"></i> Edit
        </button>
        <button
          :disabled="!selectedItem || showForm"
          @click="startCopy"
          class="btn-action"
          title="Copy"
        >
          <i class="fas fa-copy"></i> Copy
        </button>
        <button @click="startNew" class="btn-action" title="New">
          <i class="fas fa-plus"></i> New
        </button>
        <button
          :disabled="!selectedItem"
          @click="openQrCard"
          class="btn-action"
          title="QR Card"
        >
          <i class="fas fa-qrcode"></i> QR Card
        </button>
        <button
          :disabled="!selectedItem"
          @click="openLedger"
          class="btn-action"
          title="Ledger"
        >
          <i class="fas fa-book"></i> Ledger
        </button>
      </div>
    </div>

    <div class="rentme-body">
      <div v-if="showForm" class="edit-panel">
        <h3>{{ isNew ? 'New Item' : 'Edit Item' }}</h3>
        <div class="form-grid">
          <div class="form-group full-width">
            <label>Property / location</label>
            <input
              v-model="propertySearch"
              type="text"
              placeholder="Search property address (e.g. 315 Richard, 442 Fountain)..."
              @input="searchProperties"
            />
            <select v-model="form.propertyId" class="property-select">
              <option :value="null">-- Select property --</option>
              <option
                v-for="prop in propertyOptions"
                :key="prop.id"
                :value="prop.id"
              >
                {{ prop.display }}
              </option>
            </select>
            <p class="field-hint">
              Includes house/parent addresses (e.g. home office at 315 Richard).
            </p>
          </div>
          <div class="form-group">
            <label>Item Type</label>
            <select v-model="form.item_type">
              <option value="window_ac">Window AC</option>
              <option value="portable_ac">Portable AC</option>
              <option value="space_heater">Space Heater</option>
            </select>
          </div>
          <div class="form-group">
            <label>Monthly Rate ($)</label>
            <input v-model.number="form.monthly_rate" type="number" min="0" step="0.01" />
          </div>
          <div class="form-group full-width">
            <label>Description</label>
            <textarea v-model="form.description" rows="2" maxlength="500"></textarea>
          </div>
          <div class="form-group">
            <label>Serial No</label>
            <input
              ref="serialInput"
              v-model="form.serial_no"
              type="text"
              maxlength="64"
            />
          </div>
          <div class="form-group">
            <label>Model No</label>
            <input v-model="form.model_no" type="text" maxlength="64" />
          </div>
          <div class="form-group">
            <label>Minimum Months</label>
            <input
              v-model.number="form.min_months"
              type="number"
              min="1"
              @input="endsOnManuallyEdited = false"
            />
          </div>
          <div class="form-group">
            <label>Starts On</label>
            <input
              v-model="form.starts_on"
              type="date"
              @input="endsOnManuallyEdited = false"
            />
          </div>
          <div class="form-group">
            <label>Ends On</label>
            <input
              v-model="form.ends_on"
              type="date"
              @input="endsOnManuallyEdited = true"
            />
          </div>
          <div class="form-group purchasable-row">
            <label class="checkbox-label">
              <input v-model="form.purchasable" type="checkbox" />
              Purchasable
            </label>
          </div>
          <div v-if="form.purchasable" class="form-group">
            <label>Purchase Amt. ($)</label>
            <input
              v-model.number="form.purchase_amt"
              type="number"
              min="0.01"
              step="0.01"
            />
          </div>
        </div>
        <div v-if="formError" class="error-text">{{ formError }}</div>
        <div class="button-group">
          <button @click="saveItem" class="btn-primary" :disabled="saving">
            {{ saving ? 'Saving...' : 'Save' }}
          </button>
          <button @click="cancelForm" class="btn-secondary">Cancel</button>
          <button
            v-if="!isNew && selectedItem"
            @click="deleteItem"
            class="btn-danger"
          >
            Delete
          </button>
        </div>
      </div>

      <div v-else class="card-list">
        <div v-if="loading" class="loading-msg">Loading...</div>
        <div v-else-if="items.length === 0" class="empty-msg">No items found.</div>
        <div
          v-for="item in items"
          :key="item.id"
          class="item-card"
          :class="{ selected: selectedItem && selectedItem.id === item.id }"
          @click="selectItem(item)"
          @dblclick="editItem(item)"
        >
          <div class="card-line primary">
            {{ item.address }} · {{ formatItemType(item.item_type) }} ·
            ${{ formatMoney(item.monthly_rate) }}/mo · {{ item.min_months }} mo min
          </div>
          <div class="card-line secondary">
            {{ item.description }}
            <span v-if="item.serial_no"> · {{ item.serial_no }}</span>
            <span v-if="item.model_no"> · {{ item.model_no }}</span>
          </div>
          <div class="card-line dates">
            <span>
              {{ formatDate(item.starts_on) }} – {{ formatDate(item.ends_on) }}
              <span v-if="item.purchasable" class="purchase-badge">
                · purchasable ${{ formatMoney(item.purchase_amt) }}
              </span>
            </span>
            <span class="signed">
              Signed for by: {{ item.signed_by_name || '—' }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- QR Card print modal -->
    <div v-if="showQrModal" class="modal-overlay" @click.self="showQrModal = false">
      <div class="qr-modal">
        <div class="qr-modal-header">
          <h3>QR Card — Print Preview</h3>
          <button @click="showQrModal = false" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div
          id="rentme-print-card"
          class="print-card"
          :class="{ 'print-card-purchasable': selectedItem?.purchasable }"
        >
          <div class="print-card-border">
            <div class="print-card-content">
              <div class="print-text">
                <div class="print-title">Rent Me!</div>
                <div class="print-body">
                  You can rent this unit and add it to your monthly apartment
                  rent.<br />
                  <br />
                  Monthly rental:
                  <strong>${{ formatMoney(selectedItem?.monthly_rate) }}</strong>
                  a month for
                  <strong>{{ selectedItem?.min_months }}</strong> months minimum
                  (<strong>${{ formatPrintTotal() }}</strong> total)<br />
                  <template v-if="selectedItem?.purchasable">
                    <br />
                    You can also purchase this unit for
                    <strong>${{ formatMoney(selectedItem?.purchase_amt) }}</strong>
                    in three monthly installments of
                    <strong>${{ formatPrintInstallment() }}</strong>.<br />
                  </template>
                  <br />
                  scan code for details.
                </div>
              </div>
              <div ref="qrCodeContainer" class="qr-container"></div>
            </div>
          </div>
        </div>
        <div class="qr-modal-actions">
          <button @click="printQrCard" class="btn-primary">
            <i class="fas fa-print"></i> Print
          </button>
          <button
            @click="copyTestUrl"
            class="btn-secondary"
            :disabled="!securityKey"
            title="Copy dev URL to clipboard for tenant-flow testing"
          >
            <i class="fas fa-vial"></i> Test
          </button>
          <button @click="showQrModal = false" class="btn-secondary">Close</button>
        </div>
        <div v-if="testUrlMsg" class="test-url-msg">{{ testUrlMsg }}</div>
      </div>
    </div>

    <!-- Ledger modal -->
    <div v-if="showLedgerModal" class="modal-overlay" @click.self="showLedgerModal = false">
      <div class="ledger-modal">
        <div class="ledger-modal-header">
          <h3>Ledger — {{ selectedItem?.address }}</h3>
          <button @click="showLedgerModal = false" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div v-if="ledgerLoading" class="loading-msg">Loading ledger...</div>
        <div v-else-if="ledgerError" class="error-text">{{ ledgerError }}</div>
        <div v-else class="ledger-body">
          <p class="ledger-info">
            Tenant: <strong>{{ ledgerData?.agreement?.first_name }} {{ ledgerData?.agreement?.last_name }}</strong>
            · Description: <strong>{{ ledgerData?.description }}</strong>
          </p>
          <div class="ledger-summary">
            <span>{{ ledgerData?.ledgerRows?.length || 0 }} existing row(s)</span>
            <span v-if="ledgerData?.missingMonths?.length">
              · {{ ledgerData.missingMonths.length }} month(s) to add
            </span>
          </div>
          <table v-if="ledgerData?.ledgerRows?.length" class="ledger-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Debit</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in ledgerData.ledgerRows" :key="row.id">
                <td>{{ formatDate(row.Datetime) }}</td>
                <td>{{ row.Description }}</td>
                <td>${{ formatMoney(row.Debit) }}</td>
              </tr>
            </tbody>
          </table>
          <div v-else class="empty-msg">No ledger rows yet for this rental.</div>
          <div class="ledger-actions">
            <button
              @click="addLedgerRows"
              class="btn-primary"
              :disabled="ledgerActionLoading || !ledgerData?.missingMonths?.length"
            >
              Add Missing Months ({{ ledgerData?.missingMonths?.length || 0 }})
            </button>
            <button
              @click="removeLedgerRows"
              class="btn-danger"
              :disabled="ledgerActionLoading || !ledgerData?.ledgerRows?.length"
            >
              Remove Entries
            </button>
          </div>
          <div v-if="ledgerActionMsg" class="success-text">{{ ledgerActionMsg }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';
import QRCode from 'qrcode';
import eventBus from './eventBus';
import { rentMeCopyForm } from '@/utils/rentMeCopy';

const EMPTY_FORM = () => ({
  propertyId: null,
  item_type: 'portable_ac',
  description: '',
  serial_no: '',
  model_no: '',
  monthly_rate: 30,
  min_months: 4,
  starts_on: '',
  ends_on: '',
  purchasable: false,
  purchase_amt: null,
});

export default {
  name: 'RentMeSetup',
  emits: ['close'],
  data() {
    return {
      items: [],
      selectedItem: null,
      searchQuery: '',
      searchTimer: null,
      loading: false,
      showForm: false,
      isNew: false,
      form: EMPTY_FORM(),
      formError: '',
      saving: false,
      propertySearch: '',
      propertyOptions: [],
      propertySearchTimer: null,
      showQrModal: false,
      qrUrl: '',
      securityKey: '',
      testUrlMsg: '',
      showLedgerModal: false,
      ledgerData: null,
      ledgerLoading: false,
      ledgerError: '',
      ledgerActionLoading: false,
      ledgerActionMsg: '',
      endsOnManuallyEdited: false,
    };
  },
  watch: {
    'form.starts_on'() {
      this.applyCalculatedEndsOn();
    },
    'form.min_months'() {
      this.applyCalculatedEndsOn();
    },
  },
  mounted() {
    this.loadItems();
    eventBus.on('rentMeSelectItem', this.onRentMeSelectItem);
  },
  beforeUnmount() {
    eventBus.off('rentMeSelectItem', this.onRentMeSelectItem);
  },
  methods: {
    async onRentMeSelectItem({ itemId }) {
      if (itemId != null) {
        await this.selectItemById(itemId);
      }
    },

    async selectItemById(itemId) {
      this.showForm = false;
      this.showQrModal = false;
      this.showLedgerModal = false;
      await this.loadItems();
      const targetId = Number(itemId);
      let item = this.items.find((i) => Number(i.id) === targetId);
      if (!item) {
        try {
          const { data } = await axios.get(`/api/rent-me/items/${targetId}`);
          item = data;
          const existing = this.items.find((i) => Number(i.id) === targetId);
          if (!existing) {
            this.items = [item, ...this.items];
          }
        } catch (err) {
          console.error('Error loading rent-me item:', err);
          return;
        }
      }
      this.selectedItem = item;
      this.$nextTick(() => {
        const el = this.$el.querySelector('.item-card.selected');
        el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    },
    debouncedSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.loadItems(), 300);
    },

    async loadItems() {
      this.loading = true;
      try {
        const params = {};
        if (this.searchQuery.trim()) params.search = this.searchQuery.trim();
        const { data } = await axios.get('/api/rent-me/items', { params });
        this.items = data;
        if (this.selectedItem) {
          const refreshed = data.find((i) => i.id === this.selectedItem.id);
          this.selectedItem = refreshed || null;
        }
      } catch (err) {
        console.error('Error loading rent-me items:', err);
      } finally {
        this.loading = false;
      }
    },

    selectItem(item) {
      this.selectedItem = item;
      this.showForm = false;
    },

    editItem(item) {
      this.selectItem(item);
      this.startEdit();
    },

    startNew() {
      this.isNew = true;
      this.showForm = true;
      this.endsOnManuallyEdited = false;
      this.form = EMPTY_FORM();
      this.formError = '';
      this.propertySearch = '';
      this.propertyOptions = [];
    },

    startCopy() {
      if (!this.selectedItem) return;
      this.isNew = true;
      this.showForm = true;
      this.endsOnManuallyEdited = true;
      this.formError = '';
      this.form = rentMeCopyForm(this.selectedItem, (value) =>
        this.formatDateInput(value),
      );
      this.propertySearch = this.selectedItem.address || '';
      this.searchProperties();
      this.$nextTick(() => {
        this.$refs.serialInput?.focus();
      });
    },

    startEdit() {
      if (!this.selectedItem) return;
      this.isNew = false;
      this.showForm = true;
      this.endsOnManuallyEdited = false;
      this.formError = '';
      this.form = {
        propertyId: this.selectedItem.propertyId,
        item_type: this.selectedItem.item_type,
        description: this.selectedItem.description,
        serial_no: this.selectedItem.serial_no || '',
        model_no: this.selectedItem.model_no || '',
        monthly_rate: parseFloat(this.selectedItem.monthly_rate),
        min_months: this.selectedItem.min_months,
        starts_on: this.formatDateInput(this.selectedItem.starts_on),
        ends_on: this.formatDateInput(this.selectedItem.ends_on),
        purchasable: Boolean(this.selectedItem.purchasable),
        purchase_amt: this.selectedItem.purchase_amt
          ? parseFloat(this.selectedItem.purchase_amt)
          : null,
      };
      this.propertySearch = this.selectedItem.address || '';
      this.searchProperties();
    },

    cancelForm() {
      this.showForm = false;
      this.formError = '';
    },

    calculateEndsOn(startsOn, minMonths) {
      if (!startsOn || !minMonths || minMonths < 1) return '';
      const parts = startsOn.split('-').map(Number);
      if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return '';
      const [y, m, d] = parts;
      const totalMonthIndex = m - 1 + (minMonths - 1);
      const endYear = y + Math.floor(totalMonthIndex / 12);
      const endMonth = (totalMonthIndex % 12) + 1;
      const lastDay = new Date(endYear, endMonth, 0).getDate();
      const useDay = Math.min(d, lastDay);
      return `${endYear}-${String(endMonth).padStart(2, '0')}-${String(useDay).padStart(2, '0')}`;
    },

    applyCalculatedEndsOn() {
      if (this.endsOnManuallyEdited) return;
      const calculated = this.calculateEndsOn(
        this.form.starts_on,
        this.form.min_months,
      );
      if (calculated) {
        this.form.ends_on = calculated;
      }
    },

    searchProperties() {
      clearTimeout(this.propertySearchTimer);
      this.propertySearchTimer = setTimeout(async () => {
        if (!this.propertySearch || this.propertySearch.length < 2) {
          this.propertyOptions = [];
          return;
        }
        try {
          // includeHouses=1 so parent/office addresses (e.g. 315 Richard BMT) appear
          const { data } = await axios.get('/api/properties', {
            params: { query: this.propertySearch, includeHouses: '1' },
          });
          this.propertyOptions = data;
          // Keep current selection visible even if not in this search page
          if (
            this.form.propertyId &&
            !this.propertyOptions.some((p) => p.id === this.form.propertyId) &&
            this.selectedItem?.propertyId === this.form.propertyId
          ) {
            this.propertyOptions = [
              {
                id: this.form.propertyId,
                display: this.selectedItem.address,
              },
              ...this.propertyOptions,
            ];
          }
        } catch (err) {
          console.error('Error searching properties:', err);
        }
      }, 300);
    },

    async saveItem() {
      this.formError = '';
      if (!this.form.propertyId) {
        this.formError = 'Please select a property.';
        return;
      }
      if (!this.form.description?.trim()) {
        this.formError = 'Description is required.';
        return;
      }
      if (!this.form.starts_on || !this.form.ends_on) {
        this.formError = 'Start and end dates are required.';
        return;
      }
      if (
        this.form.purchasable &&
        (!this.form.purchase_amt || this.form.purchase_amt <= 0)
      ) {
        this.formError = 'Purchase amount is required when item is purchasable.';
        return;
      }

      this.saving = true;
      try {
        if (this.isNew) {
          await axios.post('/api/rent-me/items', this.form);
        } else {
          await axios.put(`/api/rent-me/items/${this.selectedItem.id}`, this.form);
        }
        this.showForm = false;
        await this.loadItems();
      } catch (err) {
        this.formError = err.response?.data?.error || 'Failed to save item.';
      } finally {
        this.saving = false;
      }
    },

    async deleteItem() {
      if (!this.selectedItem) return;
      if (!confirm('Delete this rentable item?')) return;
      try {
        await axios.delete(`/api/rent-me/items/${this.selectedItem.id}`);
        this.selectedItem = null;
        this.showForm = false;
        await this.loadItems();
      } catch (err) {
        alert(err.response?.data?.error || 'Failed to delete item.');
      }
    },

    async openQrCard() {
      if (!this.selectedItem) return;
      try {
        const { data } = await axios.post(
          `/api/rent-me/items/${this.selectedItem.id}/security-key`,
        );
        this.qrUrl = data.qrUrl;
        this.securityKey = data.securityKey;
        this.testUrlMsg = '';
        this.showQrModal = true;
        this.$nextTick(() => this.generateQRCode());
      } catch (err) {
        alert(err.response?.data?.error || 'Failed to generate QR code.');
      }
    },

    generateQRCode() {
      const container = this.$refs.qrCodeContainer;
      if (!container || !this.qrUrl) return;
      container.innerHTML = '';
      QRCode.toCanvas(
        this.qrUrl,
        {
          width: this.selectedItem?.purchasable ? 96 : 105,
          margin: 1,
          color: { dark: '#000000', light: '#FFFFFF' },
        },
        (error, canvas) => {
          if (error) {
            console.error('QR generation error:', error);
            return;
          }
          container.appendChild(canvas);
        },
      );
    },

    buildTestUrl() {
      if (!this.selectedItem?.id || !this.securityKey) return '';
      const params = new URLSearchParams({
        id: String(this.selectedItem.id),
        key: this.securityKey,
      });
      return `${window.location.origin}/rent-me?${params.toString()}`;
    },

    async copyTestUrl() {
      const testUrl = this.buildTestUrl();
      if (!testUrl) return;

      try {
        await navigator.clipboard.writeText(testUrl);
      } catch {
        const ta = document.createElement('textarea');
        ta.value = testUrl;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }

      this.testUrlMsg = `Copied to clipboard: ${testUrl}`;
      setTimeout(() => {
        this.testUrlMsg = '';
      }, 8000);
    },

    printQrCard() {
      alert(
        'Remember to select paper type of index card in the printer dialog.',
      );
      const card = document.getElementById('rentme-print-card');
      if (!card) return;

      // Canvas pixel data is lost when copied via innerHTML — convert to img first
      let printContent = card.innerHTML;
      const canvas = card.querySelector('canvas');
      if (canvas) {
        const imgTag = `<img src="${canvas.toDataURL('image/png')}" alt="QR Code" class="qr-print-img" />`;
        printContent = printContent.replace(
          /<canvas[\s\S]*?<\/canvas>/i,
          imgTag,
        );
      }

      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html><head><title>Rent Me QR Card</title>
        <style>
          @page { size: 5in 3in landscape; margin: 0.2in; }
          html, body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
            width: 100%;
            height: 100%;
          }
          .print-card {
            width: 100%;
            height: 100%;
            box-sizing: border-box;
            padding: 0.08in;
            background: white;
          }
          .print-card-border {
            border: 2px solid #333;
            border-radius: 0.12in;
            box-sizing: border-box;
            height: 100%;
            padding: 0.1in 0.12in;
            display: flex;
            align-items: center;
          }
          .print-card-content {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 0.12in;
            width: 100%;
          }
          .print-text { flex: 1; min-width: 0; text-align: left; }
          .print-title {
            font-size: 26pt;
            font-weight: bold;
            margin-bottom: 0.04in;
            line-height: 1;
          }
          .print-body { font-size: 12pt; line-height: 1.2; }
          .print-card-purchasable .print-title { font-size: 22pt; }
          .print-card-purchasable .print-body { font-size: 12pt; line-height: 1.15; }
          .qr-container { flex: 0 0 auto; }
          .qr-print-img { max-width: 1.05in; height: auto; display: block; }
          .print-card-purchasable .qr-print-img { max-width: 0.95in; }
        </style></head><body>
        ${printContent}
        </body></html>
      `);
      printWindow.document.close();
      printWindow.focus();
      const triggerPrint = () => {
        printWindow.print();
        printWindow.close();
      };
      const img = printWindow.document.querySelector('.qr-print-img');
      if (img && !img.complete) {
        img.onload = triggerPrint;
        img.onerror = triggerPrint;
      } else {
        setTimeout(triggerPrint, 250);
      }
    },

    async openLedger() {
      if (!this.selectedItem) return;
      if (!this.selectedItem.signed_by_name) {
        alert('This item has not been signed for by a tenant yet.');
        return;
      }
      this.showLedgerModal = true;
      this.ledgerLoading = true;
      this.ledgerError = '';
      this.ledgerActionMsg = '';
      try {
        const { data } = await axios.get(
          `/api/rent-me/items/${this.selectedItem.id}/ledger`,
        );
        this.ledgerData = data;
      } catch (err) {
        this.ledgerError = err.response?.data?.error || 'Failed to load ledger.';
        this.ledgerData = null;
      } finally {
        this.ledgerLoading = false;
      }
    },

    async addLedgerRows() {
      this.ledgerActionLoading = true;
      this.ledgerActionMsg = '';
      try {
        const { data } = await axios.post(
          `/api/rent-me/items/${this.selectedItem.id}/ledger/add`,
        );
        this.ledgerActionMsg = `Added ${data.added} ledger row(s).`;
        await this.openLedger();
      } catch (err) {
        alert(err.response?.data?.error || 'Failed to add ledger rows.');
      } finally {
        this.ledgerActionLoading = false;
      }
    },

    async removeLedgerRows() {
      if (
        !confirm(
          'Remove all rent-related ledger entries for this item? This cannot be undone easily.',
        )
      ) {
        return;
      }
      this.ledgerActionLoading = true;
      this.ledgerActionMsg = '';
      try {
        const { data } = await axios.post(
          `/api/rent-me/items/${this.selectedItem.id}/ledger/remove`,
        );
        this.ledgerActionMsg = `Removed ${data.removed} ledger row(s).`;
        await this.openLedger();
      } catch (err) {
        alert(err.response?.data?.error || 'Failed to remove ledger rows.');
      } finally {
        this.ledgerActionLoading = false;
      }
    },

    formatItemType(type) {
      const labels = {
        window_ac: 'Window AC',
        portable_ac: 'Portable AC',
        space_heater: 'Space Heater',
      };
      return labels[type] || type;
    },

    formatMoney(val) {
      if (val == null) return '0.00';
      return parseFloat(val).toFixed(2);
    },

    formatPrintTotal() {
      if (!this.selectedItem) return '0.00';
      const rate = parseFloat(this.selectedItem.monthly_rate) || 0;
      const months = parseInt(this.selectedItem.min_months, 10) || 0;
      return (rate * months).toFixed(2);
    },

    formatPrintInstallment() {
      const total = parseFloat(this.selectedItem?.purchase_amt) || 0;
      return (total / 3).toFixed(2);
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
      });
    },

    formatDateInput(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
  },
};
</script>

<style scoped>
.rentme-setup-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #ddd;
  font-size: 13px;
}

.title-bar {
  background: #6e5875;
  color: #fff;
  font-size: 1.4em;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top-left-radius: 5px;
  flex-shrink: 0;
}

.title-buttons {
  display: flex;
  gap: 10px;
  margin-right: 15px;
}

.title-button {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid #e1dede;
  color: #eee;
  background: #907793;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-size: 0.7em;
  cursor: pointer;
}

.title-bar button i {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.rentme-toolbar {
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-bottom: 1px solid #bbb;
}

.search-input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #aaa;
  border-radius: 4px;
  box-sizing: border-box;
}

.action-buttons {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.btn-action {
  padding: 5px 10px;
  background: #a080a8;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
}

.btn-action:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.btn-action:hover:not(:disabled) {
  background: #8a6a8f;
}

.rentme-body {
  flex: 1;
  overflow-y: auto;
  padding: 10px 14px;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.rentme-body::-webkit-scrollbar {
  display: none;
}

.card-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.item-card {
  background: #f5f5f5;
  border: 2px solid #ccc;
  border-radius: 6px;
  padding: 10px 12px;
  cursor: pointer;
  transition: border-color 0.15s;
}

.item-card:hover {
  border-color: #a080a8;
}

.item-card.selected {
  border-color: #7a5a82;
  background: #ede4ef;
}

.card-line.primary {
  font-weight: 600;
  color: #333;
}

.card-line.secondary {
  color: #555;
  margin-top: 4px;
}

.card-line.dates {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  color: #666;
  margin-top: 4px;
  font-size: 0.92em;
}

.card-line.dates .signed {
  flex-shrink: 0;
  text-align: right;
}

.edit-panel {
  background: #f9f9f9;
  padding: 12px;
  border-radius: 6px;
  box-sizing: border-box;
  overflow: hidden;
}

.edit-panel h3 {
  margin: 0 0 12px 0;
  color: #a080a8;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  font-weight: 600;
  font-size: 0.9em;
}

.purchasable-row {
  justify-content: flex-start;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  cursor: pointer;
}

.checkbox-label input {
  width: auto;
}

.purchase-badge {
  color: #5a4a6a;
  font-weight: 600;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 5px 8px;
  border: 1px solid #aaa;
  border-radius: 4px;
}

.property-select {
  margin-top: 4px;
}

.field-hint {
  margin: 4px 0 0;
  font-size: 0.8rem;
  color: #666;
  line-height: 1.3;
}

.button-group {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.btn-primary {
  background: #a080a8;
  color: white;
  border: none;
  padding: 6px 14px;
  border-radius: 4px;
  cursor: pointer;
}

.btn-secondary {
  background: #bbb;
  color: #333;
  border: none;
  padding: 6px 14px;
  border-radius: 4px;
  cursor: pointer;
}

.btn-danger {
  background: #c07070;
  color: white;
  border: none;
  padding: 6px 14px;
  border-radius: 4px;
  cursor: pointer;
}

.error-text {
  color: #c00;
  margin-top: 8px;
}

.success-text {
  color: #2a7a2a;
  margin-top: 8px;
}

.loading-msg,
.empty-msg {
  color: #666;
  padding: 20px;
  text-align: center;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.ledger-modal {
  background: #f0f0f0;
  border-radius: 8px;
  padding: 16px;
  max-width: 520px;
  width: 90%;
  max-height: 85vh;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
}

.ledger-modal::-webkit-scrollbar {
  display: none;
}

.qr-modal {
  background: #f0f0f0;
  border-radius: 8px;
  padding: 20px;
  width: min(95vw, 680px);
  max-height: 90vh;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
}

.qr-modal::-webkit-scrollbar {
  display: none;
}

.qr-modal-header,
.ledger-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.2em;
  cursor: pointer;
  color: #666;
}

.qr-modal-header h3 {
  font-size: 2em;
  margin: 0;
}

.print-card {
  background: white;
  border: 1px dashed #bbb;
  width: 5in;
  height: 3in;
  padding: 0.2in;
  margin: 0 auto;
  box-sizing: border-box;
}

.print-card-border {
  border: 2px solid #333;
  border-radius: 12px;
  box-sizing: border-box;
  height: 100%;
  padding: 0.12in 0.14in;
  display: flex;
  align-items: center;
}

.print-card-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}

.print-text {
  flex: 1;
  min-width: 0;
  text-align: left;
}

.print-title {
  font-size: 26pt;
  font-weight: bold;
  margin-bottom: 4px;
  line-height: 1;
}

.print-body {
  font-size: 12pt;
  line-height: 1.2;
  color: #222;
}

.print-card-purchasable .print-title {
  font-size: 22pt;
}

.print-card-purchasable .print-body {
  font-size: 12pt;
  line-height: 1.15;
}

.qr-container {
  display: flex;
  justify-content: center;
  flex: 0 0 auto;
}

.qr-modal-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 12px;
}

.test-url-msg {
  margin-top: 10px;
  font-size: 0.85em;
  color: #444;
  word-break: break-all;
  text-align: center;
}

.ledger-table {
  width: 100%;
  border-collapse: collapse;
  margin: 10px 0;
  font-size: 0.9em;
}

.ledger-table th,
.ledger-table td {
  border: 1px solid #ccc;
  padding: 5px 8px;
  text-align: left;
}

.ledger-table th {
  background: #e8e0ea;
}

.ledger-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.ledger-info,
.ledger-summary {
  margin: 0 0 8px 0;
  color: #444;
}
</style>